import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BgCheckDataForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    legal_first_name: "",
    legal_last_name: "",
    legal_middle_name: "",
    phone: "",
    work_state: "",
    consent: false,
    confirmed_email: "",
    previous_legal_names: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/public/bgcheck-data/${token}/info`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || "Could not load this link.");
        }
        const data = await r.json();
        setInfo(data);
        if (data.already_submitted) {
          setSubmitted(true);
        } else {
          setForm((f) => ({ ...f, ...(data.prefill || {}) }));
        }
      } catch (e) {
        setError(e.message || "Could not load this link.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setError("You must consent to the background check before submitting.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch(`${API}/public/bgcheck-data/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Submission failed.");
      }
      setSubmitted(true);
    } catch (e2) {
      setError(e2.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main
        data-testid="bgcheck-data-loading"
        className="flex min-h-screen items-center justify-center"
      >
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </main>
    );
  }
  if (!info && error) {
    return (
      <main className="mx-auto max-w-lg px-6 py-16">
        <div
          data-testid="bgcheck-data-error"
          className="border border-rose-200 bg-rose-50 p-6 text-rose-800"
        >
          <AlertTriangle className="mb-2 h-5 w-5" />
          <h1 className="font-heading text-xl font-semibold">Link unavailable</h1>
          <p className="mt-2 text-sm">{error}</p>
        </div>
      </main>
    );
  }

  const employer = info?.employer || {};
  const brandLabel = employer.label || "Your employer";

  return (
    <main
      data-testid="bgcheck-data-page"
      className="mx-auto max-w-xl px-6 py-12"
    >
      <header className="mb-8 flex items-center gap-3">
        {employer.logo_url ? (
          <img
            src={
              employer.logo_url.startsWith("http")
                ? employer.logo_url
                : `${process.env.REACT_APP_BACKEND_URL}${employer.logo_url}`
            }
            alt={brandLabel}
            className="h-10 w-auto"
          />
        ) : null}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700">
            Background check details
          </div>
          <h1 className="font-heading text-2xl font-semibold">{brandLabel}</h1>
        </div>
      </header>

      {submitted ? (
        <div
          data-testid="bgcheck-data-success"
          className="border border-emerald-200 bg-emerald-50 p-6 text-emerald-900"
        >
          <CheckCircle2 className="mb-2 h-6 w-6" />
          <h2 className="font-heading text-xl font-semibold">
            Thanks — we have what we need.
          </h2>
          <p className="mt-2 text-sm">
            {brandLabel} will now initiate your background check with Checkr.
            You&apos;ll receive a separate email directly from Checkr requesting
            sensitive information (date of birth, SSN). That information goes
            to Checkr, not to {brandLabel}.
          </p>
          {info?.application_ref && (
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-emerald-700">
              Reference:{" "}
              <span className="font-mono text-emerald-900">
                {info.application_ref}
              </span>
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5">
          <p className="text-sm text-slate-700">
            Before we send your information to Checkr, please confirm your{" "}
            <strong>legal name</strong> exactly as it appears on government-
            issued ID, the <strong>US state</strong> you&apos;ll be working in, and
            your consent. It takes about 60 seconds.
          </p>

          <div
            data-testid="bgcheck-data-info-callout"
            className="flex items-start gap-3 border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
          >
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-600" />
            <div>
              <strong>Heads up: we don&apos;t collect DOB or SSN here.</strong>{" "}
              After you submit this form, you&apos;ll get a separate email
              directly from <strong>Checkr</strong> (the screening company)
              asking for your date of birth, Social Security Number, and
              address history. Those sensitive fields go to Checkr&apos;s secure
              portal — never to {brandLabel} or to Hireloop.
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Legal first name *
              </span>
              <input
                data-testid="bgcheck-data-first-name"
                value={form.legal_first_name}
                onChange={(e) =>
                  setForm({ ...form, legal_first_name: e.target.value })
                }
                required
                minLength={2}
                maxLength={60}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Legal last name *
              </span>
              <input
                data-testid="bgcheck-data-last-name"
                value={form.legal_last_name}
                onChange={(e) =>
                  setForm({ ...form, legal_last_name: e.target.value })
                }
                required
                minLength={2}
                maxLength={60}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Legal middle name (optional)
              </span>
              <input
                data-testid="bgcheck-data-middle-name"
                value={form.legal_middle_name}
                onChange={(e) =>
                  setForm({ ...form, legal_middle_name: e.target.value })
                }
                maxLength={60}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Phone (optional)
              </span>
              <input
                data-testid="bgcheck-data-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 123 4567"
                maxLength={32}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Work state (US) *
              </span>
              <select
                data-testid="bgcheck-data-work-state"
                value={form.work_state}
                onChange={(e) =>
                  setForm({ ...form, work_state: e.target.value })
                }
                required
                className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
              >
                <option value="">— Pick a state —</option>
                {(info?.us_states || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Confirm email on file (optional)
              </span>
              <input
                data-testid="bgcheck-data-confirmed-email"
                type="email"
                value={form.confirmed_email}
                onChange={(e) =>
                  setForm({ ...form, confirmed_email: e.target.value })
                }
                placeholder={info?.candidate_email || "you@example.com"}
                maxLength={120}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                Re-type your email if you spot a typo above ({info?.candidate_email || "—"}). Checkr&apos;s invite will go to this address.
              </span>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700">
                Other legal names you&apos;ve used (optional)
              </span>
              <input
                data-testid="bgcheck-data-previous-names"
                value={form.previous_legal_names}
                onChange={(e) =>
                  setForm({ ...form, previous_legal_names: e.target.value })
                }
                placeholder="e.g. maiden name: Smith · or 'none'"
                maxLength={240}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500"
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                Helps Checkr verify records filed under a prior surname (marriage / legal name change).
              </span>
            </label>
          </div>

          <label className="flex items-start gap-3 border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <input
              type="checkbox"
              data-testid="bgcheck-data-consent"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 h-4 w-4 accent-emerald-600"
              required
            />
            <span>
              <strong>I consent</strong> to a background check being initiated
              for me as part of my application to <strong>{brandLabel}</strong>
              . I understand a separate request will be sent directly from{" "}
              <strong>Checkr</strong> (the screening company) asking for
              date-of-birth, SSN, and address — that sensitive information
              goes to Checkr, not to {brandLabel}.
            </span>
          </label>

          {error && (
            <div
              data-testid="bgcheck-data-submit-error"
              className="border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 pt-5">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Encrypted submission · Hireloop never stores DOB or SSN
            </div>
            <button
              type="submit"
              data-testid="bgcheck-data-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2 text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Submit
            </button>
          </div>
        </form>
      )}

      {info && !employer.hide_powered_by && (
        <p className="mt-10 text-center text-[10px] uppercase tracking-[0.22em] text-slate-400">
          Powered by Hireloop PRO
        </p>
      )}
    </main>
  );
}
